import {
  useDataProvider,
  useGetIdentity,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import {
  type ApproveDayTicketBody,
  type ApproveGroupBody,
  FESTIVAL_STATUSES,
  type FestivalListRow,
  type FestivalPageRead,
  type FestivalPreviewRead,
  type FestivalSplitPageResult,
  type PreviewBody,
  type PublishChangesBody,
  type PublishRetryBody,
  type SuggestionAcceptBody,
} from "@/types/tina-festival-page";

/** Cache key root for everything on the festival screen that is not the Refine tables themselves. */
export const FESTIVAL_QUERY_ROOT = "tina-festivals";

/**
 * What a festival write came to. Every answer the screen has a sentence for is a value; the rest throw.
 *
 * - `stale`: 409 `skeleton_changed` (the proposal was rebuilt under the curator's form) or `page_changed` (the stored
 *   page is not the version sent). Reload for the new digest or version.
 * - `refused`: 422 `{"problems": [...]}`, an answer the curator can fix. Never an empty list.
 * - `conflict`: any other 409. `detail` is the server's string, shown through `conflictText`.
 * - `no_identity`: nothing was sent, because the curator's uid has not loaded.
 */
export type ResolveOutcome<T = unknown> =
  | { kind: "ok"; data: T }
  | { kind: "stale" }
  | { kind: "refused"; problems: string[] }
  | { kind: "conflict"; detail: string }
  | { kind: "no_identity" };

/** Every outcome but `ok`: the answers a failed request can stand for. */
type NotOk = Exclude<ResolveOutcome, { kind: "ok" }>;

/** The status and text of whatever a failed request threw (the provider's `Error(detail)` with `statusCode`). */
function readFailure(error: unknown): { status?: number; message?: string } {
  if (typeof error !== "object" || error === null) return {};
  const { statusCode, message } = error as {
    statusCode?: unknown;
    message?: unknown;
  };
  return {
    status: typeof statusCode === "number" ? statusCode : undefined,
    message: typeof message === "string" ? message : undefined,
  };
}

/**
 * The `problems` of a 422 message, or null when it is not one.
 *
 * Plan 5 answers an answer the curator can fix as `{"detail": {"problems": [...]}}`, which the provider
 * JSON-stringifies into `message`. FastAPI's own validation 422 is a list (rendered as plain text) and carries no
 * problems at all: it is a bug, so it stays a thrown error. A list that is empty, or holds anything but strings, is
 * not accepted either, so a refusal never reaches the screen with nothing to show.
 */
function problemsOf(message: string): string[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(message);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const { problems } = parsed as { problems?: unknown };
  if (!Array.isArray(problems) || problems.length === 0) return null;
  if (!problems.every((problem) => typeof problem === "string")) return null;
  return problems as string[];
}

/** The outcome an error stands for, or null when it is a real failure to throw. */
function outcomeOf(error: unknown): NotOk | null {
  const { status, message } = readFailure(error);
  if (message === undefined) return null;
  if (status === 409) {
    if (message === "skeleton_changed" || message === "page_changed") {
      return { kind: "stale" };
    }
    return { kind: "conflict", detail: message };
  }
  // A suggestion another tab answered, or the job closed, is gone: a sentence, not a crash.
  if (status === 404 && message === "suggestion_not_found") {
    return { kind: "conflict", detail: message };
  }
  if (status === 422) {
    const problems = problemsOf(message);
    return problems ? { kind: "refused", problems } : null;
  }
  return null;
}

/**
 * Everything `/tina/festivals` reads and writes, in the shape every other Tina surface's repository hook takes: the
 * data provider's `custom` for routes Refine has no CRUD verb for, Sentry breadcrumbs around each write, and a real
 * failure surfaced through `toHumanErrorMessage` rather than swallowed.
 *
 * The list (`tina-festival-pages`) is read by `useTable`, not from here.
 */
/** Every status a festival can still be merged into: all but the two that are over. */
const MERGEABLE_STATUSES = FESTIVAL_STATUSES.filter(
  (status) => status !== "rejected" && status !== "ended_unapproved",
);

export const useFestivalRepository = () => {
  const { custom } = useDataProvider()("tina");
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const { data: identity } = useGetIdentity<IIdentity>();

  /**
   * The signed-in curator's Firebase uid, the `resolved_by` of every write. The server refuses a blank one and there is
   * no "unknown" actor to fall back on: a placeholder in an actor column is what put phantom curators on the HITL
   * leaderboard, so an identity that has not loaded blocks the write instead of inventing one.
   */
  const resolvedBy = identity?.id;

  async function read<T>(urlPath: string): Promise<T> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath },
    });
    return response.data as T;
  }

  /**
   * Mark the festival list and every page read on the screen stale, so each refetches. A write does this on `ok`
   * itself; a caller uses it when an answer wrote nothing but showed that what the list holds is out of date.
   */
  function invalidateFestivals(): void {
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-festival-pages",
      invalidates: ["list"],
    });
    void queryClient.invalidateQueries({ queryKey: [FESTIVAL_QUERY_ROOT] });
  }

  /**
   * One festival write: the uid added to the body, a breadcrumb, then the answer mapped to an outcome. Only `ok`
   * invalidates, because a refusal wrote nothing. A failure with no outcome is shown and rethrown.
   */
  async function write<T>(
    action: string,
    urlPath: string,
    body: object,
    failureTitle: string,
    breadcrumb: Record<string, unknown>,
  ): Promise<ResolveOutcome<T>> {
    if (!resolvedBy) {
      open?.({
        type: "error",
        message: "Cannot save yet",
        description:
          "Your account has not finished loading, and a festival change has to be attributable. Reload the page and try again.",
      });
      return { kind: "no_identity" };
    }
    Sentry.addBreadcrumb({
      category: `tina-festivals.${action}`,
      message: `Festival ${action}`,
      level: "info",
      data: breadcrumb,
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { ...body, resolved_by: resolvedBy },
        meta: { urlPath },
      });
      invalidateFestivals();
      return { kind: "ok", data: response.data as T };
    } catch (error) {
      const outcome = outcomeOf(error);
      if (outcome) return outcome;
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: failureTitle,
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  // ── The one page (`/festivals/pages/*`) ───────────────────────────────────

  const pageUrl = (recordId: string) =>
    `festivals/pages/${encodeURIComponent(recordId)}`;

  const fetchPage = (recordId: string) =>
    read<FestivalPageRead>(pageUrl(recordId));

  /** The record a card belongs to: the old card url's redirect target. */
  const fetchCardRecordId = async (cardId: string): Promise<string> => {
    const detail = await read<{ card: { record_id: string } }>(
      `festivals/cards/${encodeURIComponent(cardId)}`,
    );
    return detail.card.record_id;
  };

  /** The festivals a festival can be merged into; Tina judges the merge itself. */
  const fetchMergeCandidates = async (): Promise<FestivalListRow[]> => {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath: "festivals/pages" },
      query: { status: MERGEABLE_STATUSES, limit: 200, offset: 0 },
    });
    const items = (response.data as { items?: unknown } | undefined)?.items;
    return Array.isArray(items) ? (items as FestivalListRow[]) : [];
  };

  type PageOutcome = ResolveOutcome<FestivalPageRead>;

  const pageWrite = (
    action: string,
    recordId: string,
    route: string,
    body: object,
    failureTitle: string,
    breadcrumb: Record<string, unknown> = {},
  ): Promise<PageOutcome> =>
    write<FestivalPageRead>(
      action,
      `${pageUrl(recordId)}/${route}`,
      body,
      failureTitle,
      { recordId, ...breadcrumb },
    );

  const approve = (recordId: string, body: ApproveDayTicketBody) =>
    pageWrite(
      "approve",
      recordId,
      "approve",
      body,
      "Failed to approve the festival",
    );

  const approveGroup = (recordId: string, body: ApproveGroupBody) =>
    pageWrite(
      "approve-group",
      recordId,
      "approve-group",
      body,
      "Failed to approve the group",
    );

  const reject = (recordId: string) =>
    pageWrite(
      "reject",
      recordId,
      "reject",
      {},
      "Failed to reject the festival",
    );

  const retryPublish = (recordId: string, body: PublishRetryBody) =>
    pageWrite(
      "publish-retry",
      recordId,
      "publish/retry",
      body,
      "Failed to retry the publish",
    );

  const publishChanges = (recordId: string, body: PublishChangesBody) =>
    pageWrite(
      "publish-changes",
      recordId,
      "publish-changes",
      body,
      "Failed to publish the changes",
    );

  const acceptSuggestion = (
    recordId: string,
    suggestionId: string,
    body: SuggestionAcceptBody,
  ) =>
    pageWrite(
      "suggestion-accept",
      recordId,
      `suggestions/${encodeURIComponent(suggestionId)}/accept`,
      body,
      "Failed to accept the suggestion",
      { suggestionId },
    );

  const dismissSuggestion = (recordId: string, suggestionId: string) =>
    pageWrite(
      "suggestion-dismiss",
      recordId,
      `suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
      {},
      "Failed to dismiss the suggestion",
      { suggestionId },
    );

  const chooseKind = (recordId: string, kind: "day_ticket" | "program") =>
    pageWrite(
      "page-kind",
      recordId,
      "kind",
      { kind },
      "Failed to change the festival kind",
      { kind },
    );

  /** Fold a festival into another; `ok` carries the KEPT festival's page. */
  const mergePage = (recordId: string, intoRecordId: string) =>
    pageWrite(
      "page-merge",
      recordId,
      "merge",
      { into_record_id: intoRecordId },
      "Failed to merge the festivals",
      { intoRecordId },
    );

  /** Move pages out into a new festival; `ok` carries the original's page and the new festival's id. */
  const splitPage = (recordId: string, urls: string[]) =>
    write<FestivalSplitPageResult>(
      "page-split",
      `${pageUrl(recordId)}/split`,
      { urls },
      "Failed to split the festival",
      { recordId, pageCount: urls.length },
    );

  const unpublish = (recordId: string) =>
    pageWrite(
      "unpublish",
      recordId,
      "unpublish",
      {},
      "Failed to unpublish the festival",
    );

  /**
   * What Tina would make for the body IMS would send. A read in all but verb: Tina validates and rolls back. So it is
   * not a `write()`: no breadcrumb, no invalidation (it would refetch the page on every pause in typing), no toast.
   * Any failure throws the provider's `Error` with its `statusCode`, for the caller to read.
   */
  const previewPage = async (
    recordId: string,
    body: PreviewBody,
  ): Promise<FestivalPreviewRead> => {
    if (!resolvedBy) throw new Error("no_identity");
    const response = await custom!({
      url: "",
      method: "post",
      payload: { ...body, resolved_by: resolvedBy },
      meta: { urlPath: `${pageUrl(recordId)}/preview` },
    });
    const data = response.data as Partial<FestivalPreviewRead> | undefined;
    return {
      lines: Array.isArray(data?.lines) ? data.lines : [],
      problems: Array.isArray(data?.problems) ? data.problems : [],
    };
  };

  return {
    fetchPage,
    fetchCardRecordId,
    fetchMergeCandidates,
    approve,
    approveGroup,
    reject,
    retryPublish,
    publishChanges,
    acceptSuggestion,
    dismissSuggestion,
    chooseKind,
    mergePage,
    splitPage,
    unpublish,
    previewPage,
    invalidateFestivals,
    resolvedBy,
  };
};
